import {assert, assertAndContinue} from 'Asserts';
import {MixtureMeasurementUnit} from 'graphql-client/__generated/graphqlGlobalTypes';
import {IngredientDataFromParent} from 'notebook/integrations/registry/MixtureGroupConstants';
import {
  COMPONENT_BIOENTITY_COLUMN_NAME,
  MixtureIngredientsStructuredTable,
  MixtureIngredientsStructuredTableRowMetadata,
} from 'notebook/integrations/registry/MixtureIngredientsStructuredTableAdapter';
import {MixtureMetadataStructuredTable} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {
  INGREDIENT_CATALOG_IDENTIFIER_COLUMN_NAME,
  UNITS_COLUMN_NAME,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import addRowsToTable from 'notebook/structured-tables/util/addRowsToTable';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {getTableWithUpdatedCellByColumnType} from 'notebook/structured-tables/util/StructuredTableUpdaters';
import {Row} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {getTypedValueFromBioentitySummary} from 'notebook/tables/typed-values/DataTypes';

import {getMixtureIngredientsStructuredTableAdapter} from './MixtureTableAdapters';

export function getUpdatedTableWithUnitsFromParent(
  table: MixtureMetadataStructuredTable,
  units: MixtureMeasurementUnit
): MixtureMetadataStructuredTable {
  assert(table.rows.length === 1, 'Expected MixtureMetadataStructuredTable to have exactly 1 row');
  return getTableWithUpdatedCellByColumnType(table, 0, TableColumnType.UNIT_SELECTOR, {
    text: '',
    typedValue: {rawValue: units, type: 'STRING'},
  });
}

export function getUpdatedTableWithIngredientRowsFromParent(
  table: MixtureIngredientsStructuredTable,
  ingredientsFromParent: Array<IngredientDataFromParent>
): MixtureIngredientsStructuredTable {
  const componentBioentityColumnIndex = table.columns.findIndex(
    (column) =>
      column.columnType === TableColumnType.EXISTING_BIOENTITY &&
      column.columnGroup == null &&
      column.name === COMPONENT_BIOENTITY_COLUMN_NAME
  );
  assert(
    componentBioentityColumnIndex !== -1,
    'Expected mixture ingredients table to have a component column'
  );

  const catalogIdentifierColumnIndex = table.columns.findIndex(
    (column) =>
      column.columnType === TableColumnType.INGREDIENT_CATALOG_ID &&
      column.name === INGREDIENT_CATALOG_IDENTIFIER_COLUMN_NAME
  );
  assert(
    catalogIdentifierColumnIndex !== -1,
    'Expected mixture ingredients table to have a catalog identifier column'
  );

  const unitsColumnIndex = table.columns.findIndex(
    (column) => column.columnType === TableColumnType.UNIT_SELECTOR && column.name === UNITS_COLUMN_NAME
  );
  assert(unitsColumnIndex !== -1, 'Expected mixture ingredients table to have a units column');

  const notesColumnIndex = table.columns.findIndex(
    (column) => column.columnType === TableColumnType.INGREDIENT_NOTES
  );

  const newRows: Array<Row> = [];
  const newRowMetadata: Array<MixtureIngredientsStructuredTableRowMetadata> = [];
  ingredientsFromParent.forEach((ingredientFromParent, i) => {
    const {
      componentBioentitySummary,
      units,
      catalogIdentifier,
      parentIngredientId,
      parentIngredientAmount,
      notes,
    } = ingredientFromParent;
    const row = getBlankRow(i, table.columns, null);
    row[componentBioentityColumnIndex] = {
      text: '',
      typedValue: getTypedValueFromBioentitySummary(componentBioentitySummary),
    };
    row[catalogIdentifierColumnIndex] = {text: catalogIdentifier || ''};
    row[unitsColumnIndex] = {text: units};
    if (notesColumnIndex !== -1) {
      // The user is allowed to delete this column
      row[notesColumnIndex] = {text: notes || ''};
    }
    newRows.push(row);
    newRowMetadata.push({
      parentIngredientId,
      parentIngredientAmount,
      isCatalogIdentifierInherited: catalogIdentifier != null,
      ingredientId: null,
    });
  });
  // TODO(BNCH-136540): Add table edit up support for Mixture Prep tables
  const addRowsResult = addRowsToTable(
    newRows,
    table,
    getMixtureIngredientsStructuredTableAdapter(),
    newRowMetadata,
    {
      addAtBeginning: true,
    }
  );
  assertAndContinue(
    addRowsResult != null,
    'Error adding inherited ingredient rows in MixtureIngredientsStructuredTableAdapter'
  );
  return addRowsResult?.updatedTable || table;
}

export function getUpdatedTableWithoutIngredientRowsFromParent(
  table: MixtureIngredientsStructuredTable
): MixtureIngredientsStructuredTable {
  const updatedRows = table.rows.filter((row) => row.metadata.parentIngredientId == null);
  return {
    ...table,
    rows: updatedRows.length
      ? updatedRows
      : [getMixtureIngredientsStructuredTableAdapter().getBlankRow(table.metadata, 0, table.columns)],
  };
}
